import { z } from "zod";
/**
 * Clubs: challenges among friends, on their own aggregate (`D277`, `D278`,
 * `D279`; implements `D233`'s group screens and `D234`'s ranking).
 *
 * ── A CLUB IS NOT A `D50` GROUP, ON PURPOSE ────────────────────────────────
 * A `D50` group's `owner`, `admin` and `trainer` memberships grant reach over
 * every student's training data (`resolveVisibleAccounts`). A friends'
 * challenge must grant nobody anything, so a club lives in its own tables and
 * a club membership is read by no reach check at all (`T-265`).
 *
 * ── WHAT A MEMBER SEES OF THE OTHERS ───────────────────────────────────────
 * The nickname they chose for this club, their role and team, and — only
 * while the admin has the ranking ON — their number in the current round.
 * With the ranking off (the default, `D234`) a member sees their own number,
 * the group's total and how many finished, never another person's number
 * (`D104`). No account id, e-mail or real name ever travels here.
 *
 * ── THE NUMBERS ARE THE SERVER'S ───────────────────────────────────────────
 * A round's progress is computed on each read from the sessions logged in
 * this app inside the round's days (`D152`'s "server-computed"); nothing the
 * device says is a score. `CLUB_CHALLENGES` documents what counts.
 */
export const CLUB_ROUTES = {
    /** `GET` my clubs; `POST` creates one (idempotent on the client's id). */
    clubs: "/me/clubs",
    /** `GET` — what a code would join, and the settings to accept (`D234`). */
    invite: "/me/clubs/invite/:code",
    /** `POST` — join by code, naming the settings version that was shown. */
    join: "/me/clubs/join",
    /** `GET` — clubs whose admin made the ranking global (`D277`). */
    directory: "/me/clubs/directory",
    /** `GET` — one club: members, the round, the progress. */
    club: "/me/clubs/:clubId",
    /** `POST` — leave. The owner hands the club over first, or closes it. */
    leave: "/me/clubs/:clubId/leave",
    /** `PUT` — the admin's settings: name, ranking, scope, open to join. */
    settings: "/me/clubs/:clubId/settings",
    /** `POST` — a new round with the same people (`D278`). Admin only. */
    rounds: "/me/clubs/:clubId/rounds",
    /** `PUT` role or team; `DELETE` removes the member. Admin only. */
    member: "/me/clubs/:clubId/members/:memberId",
    /** `PUT` — one reaction on or off (`D278`). */
    reactions: "/me/clubs/:clubId/reactions",
    /** `GET` the chat; `POST` one message (`D278`). */
    messages: "/me/clubs/:clubId/messages",
    /** `DELETE` — the author's own message, or any by an admin. */
    message: "/me/clubs/:clubId/messages/:messageId",
    /** `POST` — report a message to the club's admins. */
    report: "/me/clubs/:clubId/messages/:messageId/report",
};
export function clubPath(clubId) {
    return `${CLUB_ROUTES.clubs}/${encodeURIComponent(clubId)}`;
}
export function clubInvitePath(code) {
    return `${CLUB_ROUTES.clubs}/invite/${encodeURIComponent(code)}`;
}
/** The roles inside a club. Not `D50`'s four: none of these reaches data. */
export const CLUB_ROLES = ["owner", "admin", "member"];
export const clubRole = z.enum(CLUB_ROLES);
/** Two teams at most; a round with teams splits the club in two. */
export const CLUB_TEAMS = ["a", "b"];
export const clubTeam = z.enum(CLUB_TEAMS);
/**
 * The challenge templates, goals mode (`D277`). Both are counted from
 * sessions logged in this app, ended and not abandoned, with at least
 * {@link CLUB_QUALIFYING_SETS} sets still on them and at least
 * {@link CLUB_QUALIFYING_MINUTES} between start and end, STARTED inside the
 * round's days in the club's time zone. An imported session never counts.
 *
 * - `workouts` — such sessions, at most {@link CLUB_WORKOUTS_PER_DAY} a day;
 * - `active_days` — the days with at least one.
 */
export const CLUB_CHALLENGES = ["workouts", "active_days"];
export const clubChallenge = z.enum(CLUB_CHALLENGES);
export const CLUB_QUALIFYING_SETS = 3;
export const CLUB_QUALIFYING_MINUTES = 10;
export const CLUB_WORKOUTS_PER_DAY = 2;
/** Who may see a ranking when it is on (`D234`, `D277`). There is no gym
 *  scope: nothing in this app is a shared gym yet (`D202`'s gyms are one
 *  person's equipment). */
export const CLUB_RANKING_SCOPES = ["members", "global"];
export const clubRankingScope = z.enum(CLUB_RANKING_SCOPES);
/** The fixed reactions (`D278`). No free text, no counts per person. */
export const CLUB_REACTIONS = ["fire", "clap", "muscle", "heart"];
export const clubReaction = z.enum(CLUB_REACTIONS);
export const CLUB_NAME_MAX = 60;
export const CLUB_NICKNAME_MAX = 30;
export const CLUB_MESSAGE_MAX = 280;
export const CLUB_MEMBER_CAP = 50;
/** Rounds last from one to eight weeks, whole days. */
export const CLUB_ROUND_DAYS_MIN = 7;
export const CLUB_ROUND_DAYS_MAX = 56;
export const CLUB_TARGET_MAX = 100;
/** "Comece com 5 amigos" (`D279`): the club the app suggests filling. */
export const CLUB_START_WITH_FRIENDS = 5;
/** Eight characters from an alphabet with no 0/O or 1/I to misread. */
export const CLUB_INVITE_CODE_PATTERN = /^[2-9A-HJ-NP-Z]{8}$/;
export const clubInviteCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(CLUB_INVITE_CODE_PATTERN);
const clubName = z.string().trim().min(1).max(CLUB_NAME_MAX);
const nickname = z.string().trim().min(1).max(CLUB_NICKNAME_MAX);
/** An IANA zone name as the device reports it; the server checks it. */
const timeZone = z.string().min(1).max(64);
const isoDay = z.iso.date();
export const clubRoundInput = z.strictObject({
    challenge: clubChallenge,
    days: z.int().min(CLUB_ROUND_DAYS_MIN).max(CLUB_ROUND_DAYS_MAX),
    target: z.int().min(1).max(CLUB_TARGET_MAX),
    teams: z.boolean(),
});
/** `POST /me/clubs`. `id` is the device's UUIDv7, kept across a retry. */
export const createClubInput = z.strictObject({
    id: z.uuid(),
    name: clubName,
    nickname,
    round: clubRoundInput,
    timeZone,
});
/** `POST /me/clubs/join`. `settingsVersion` is the one the preview showed:
 *  an admin's change in between answers 409 `club.settings_changed`, and the
 *  app shows the new settings before asking again (`D234`). */
export const joinClubInput = z.strictObject({
    code: clubInviteCode,
    nickname,
    settingsVersion: z.int().min(1),
});
export const clubSettingsInput = z.strictObject({
    name: clubName.optional(),
    /** Anyone who finds a global club may join it without the code. */
    openToJoin: z.boolean(),
    rankingEnabled: z.boolean(),
    rankingScope: clubRankingScope,
});
export const clubMemberInput = z.strictObject({
    /** `admin` or `member`; the owner is never set here. */
    role: z.enum(["admin", "member"]).optional(),
    team: clubTeam.nullable().optional(),
});
export const clubReactionInput = z.strictObject({
    kind: clubReaction,
    on: z.boolean(),
    toMemberId: z.uuid(),
});
export const clubMessageInput = z.strictObject({
    body: z.string().trim().min(1).max(CLUB_MESSAGE_MAX),
    /** The device's UUIDv7: a resend answers the stored message. */
    id: z.uuid(),
});
export const clubSettings = z.object({
    openToJoin: z.boolean(),
    rankingEnabled: z.boolean(),
    rankingScope: clubRankingScope,
    /** Bumped by every change; the join names the one it was shown. */
    version: z.int(),
});
export const clubRound = z.object({
    challenge: clubChallenge,
    /** The round's last day, inclusive, in the club's zone. */
    endsOn: isoDay,
    id: z.uuid(),
    number: z.int(),
    startsOn: isoDay,
    status: z.enum(["running", "ended"]),
    target: z.int(),
    teams: z.boolean(),
});
export const clubSummary = z.object({
    id: z.uuid(),
    memberCount: z.int(),
    name: z.string(),
    role: clubRole,
    round: clubRound.nullable(),
});
export const clubListOutput = z.object({ clubs: z.array(clubSummary) });
export const clubMember = z.object({
    id: z.uuid(),
    isMe: z.boolean(),
    nickname: z.string(),
    role: clubRole,
    team: clubTeam.nullable(),
});
export const clubStanding = z.object({
    memberId: z.uuid(),
    nickname: z.string(),
    /** 1-based; equal values share a rank. */
    rank: z.int(),
    value: z.int(),
});
export const clubProgress = z.object({
    group: z.object({
        /** Members who reached the target this round. */
        finished: z.int(),
        total: z.int(),
    }),
    mine: z.object({ value: z.int() }),
    /** Only while the admin has the ranking on (`D234`); otherwise null. */
    standings: z.array(clubStanding).nullable(),
    /** Only on a round with teams. */
    teams: z.array(z.object({ team: clubTeam, total: z.int() })).nullable(),
});
export const clubReactionTally = z.object({
    count: z.int(),
    kind: clubReaction,
    mine: z.boolean(),
    toMemberId: z.uuid(),
});
/** `GET /me/clubs/:clubId` */
export const clubDetailOutput = z.object({
    id: z.uuid(),
    /** Every member may invite; the code is how, until links exist (`D257`). */
    inviteCode: z.string(),
    me: z.object({ memberId: z.uuid(), role: clubRole }),
    memberCap: z.int(),
    members: z.array(clubMember),
    name: z.string(),
    progress: clubProgress.nullable(),
    reactions: z.array(clubReactionTally),
    round: clubRound.nullable(),
    settings: clubSettings,
});
/** `GET /me/clubs/invite/:code` — enough to decide, nothing about anyone. */
export const clubInviteOutput = z.object({
    alreadyMember: z.boolean(),
    clubId: z.uuid(),
    full: z.boolean(),
    memberCount: z.int(),
    name: z.string(),
    round: clubRound.nullable(),
    settings: clubSettings,
});
/** `GET /me/clubs/directory` — global clubs, each with its standings top. */
export const clubDirectoryOutput = z.object({
    clubs: z.array(z.object({
        id: z.uuid(),
        /** Present only when the club is open to join: the directory's way in
         *  goes through the same preview and join as a code typed by hand. */
        inviteCode: z.string().nullable(),
        memberCount: z.int(),
        name: z.string(),
        openToJoin: z.boolean(),
        round: clubRound.nullable(),
        standings: z.array(clubStanding),
    })),
    /** False when the caller is in no club with its ranking on: the directory
     *  is shown only to people who opted into ranking themselves (`D277`). */
    visible: z.boolean(),
});
export const clubMessage = z.object({
    body: z.string(),
    id: z.uuid(),
    memberId: z.uuid().nullable(),
    mine: z.boolean(),
    nickname: z.string(),
    /** True once any member reported it; admins see it to decide. */
    reported: z.boolean(),
    sentAt: z.iso.datetime(),
});
/** `GET /me/clubs/:clubId/messages` — the last days kept, oldest first. */
export const clubMessagesOutput = z.object({
    messages: z.array(clubMessage),
});
/** How long the chat keeps a message (`D278`). */
export const CLUB_MESSAGE_RETENTION_DAYS = 30;
//# sourceMappingURL=clubs.js.map